import { create } from "zustand";
import { devtools } from "zustand/middleware";
import axios from "axios";

const initialState = {
  payslips: [],
  gettingPayslips: false,
  gettingPayslipsError: {},

  myPayslips: [],
  gettingMyPayslips: false,
  gettingMyPayslipsError: {},

  payslipDetails: {},
  gettingPayslipDetails: false,
  getPayslipDetailsError: {},

  createdPayslip: null,
  creatingPayslip: false,
  creatingPayslipError: {},

  updatedPayslip: null,
  updatingPayslip: false,
  updatingPayslipError: {},

  deletedPayslip: null,
  deletingPayslip: false,
  deletingPayslipError: {},

  approvingPayslip: false,
  approvedPayslip: null,
  approvingPayslipError: {},

  checkingPayslip: false,
  checkedPayslip: null,
  checkingPayslipError: {},

  rejectingPayslip: false,
  rejectedPayslip: null,
  rejectingPayslipError: {},

  undoingApprovePayslip: false,
  undoneApprovePayslip: null,
  undoingApprovePayslipError: {},

  downloadingPayslipPdf: false,
  downloadingPayslipPdfError: {},

  showCreatePayslip: false,
  payslipToEdit: null,
  showPayslipDetails: false,
  payslipDetailsRecord: null,

  creating: true,
};

const store = (set, get) => ({
  ...initialState,

  getAllPayslips: async () => {
    set({ gettingPayslips: true, gettingPayslipsError: {} });
    try {
      const res = await axios.get("/administration/monthly-payslips");
      const responseData = res?.data || res;
      const data = responseData?.data || responseData;
      set({ payslips: data?.payslips || data || [] });
    } catch (error) {
      set({ gettingPayslipsError: error });
    } finally {
      set({ gettingPayslips: false });
    }
  },

  getMyPayslips: async () => {
    set({ gettingMyPayslips: true, gettingMyPayslipsError: {} });
    try {
      const res = await axios.get("/administration/monthly-payslips/my-payslips");
      set({ myPayslips: res.payslips });
    } catch (error) {
      set({ gettingMyPayslipsError: error });
    } finally {
      set({ gettingMyPayslips: false });
    }
  },

  getPayslipDetails: async (id) => {
    set({ gettingPayslipDetails: true, getPayslipDetailsError: {} });
    try {
      const res = await axios.get(`/administration/monthly-payslips/${id}`);
      set({ payslipDetails: res });
    } catch (error) {
      set({ getPayslipDetailsError: error });
    } finally {
      set({ gettingPayslipDetails: false });
    }
  },

  createPayslip: async (data) => {
    set({ creatingPayslip: true, creatingPayslipError: {}, createdPayslip: null });
    try {
      const res = await axios.post("/administration/monthly-payslips", data);
      set({ createdPayslip: res.data });
      get().getAllPayslips();
      get().getMyPayslips();
      get().setCreatingPayslip(false, false, null);
    } catch (error) {
      set({ creatingPayslipError: error });
    } finally {
      set({ creatingPayslip: false });
    }
  },

  updatePayslip: async (id, data) => {
    set({ updatingPayslip: true, updatingPayslipError: {}, updatedPayslip: null });
    try {
      const res = await axios.put(`/administration/monthly-payslips/${id}`, data);
      set({ updatedPayslip: res.data });
      get().getAllPayslips();
      get().setCreatingPayslip(false, false, null);
    } catch (error) {
      set({ updatingPayslipError: error });
    } finally {
      set({ updatingPayslip: false });
    }
  },

  deletePayslip: async (id) => {
    set({ deletingPayslip: true, deletingPayslipError: {}, deletedPayslip: null });
    try {
      const res = await axios.delete(`/administration/monthly-payslips/${id}`);
      set({ deletedPayslip: res.data });
      get().getAllPayslips();
    } catch (error) {
      set({ deletingPayslipError: error });
    } finally {
      set({ deletingPayslip: false });
    }
  },

  approvePayslip: async (id, data = {}) => {
    set({ approvingPayslip: true, approvingPayslipError: {}, approvedPayslip: null });
    try {
      const res = await axios.patch(`/administration/monthly-payslips/approve/${id}`, data);
      set({ approvedPayslip: res.data });
      get().getAllPayslips();
      get().getPayslipDetails(id);
    } catch (error) {
      set({ approvingPayslipError: error });
    } finally {
      set({ approvingPayslip: false });
    }
  },

  checkPayslip: async (id, data = {}) => {
    set({ checkingPayslip: true, checkingPayslipError: {}, checkedPayslip: null });
    try {
      const res = await axios.patch(`/administration/monthly-payslips/check/${id}`, data);
      set({ checkedPayslip: res.data });
      get().getAllPayslips();
      get().getPayslipDetails(id);
    } catch (error) {
      set({ checkingPayslipError: error });
    } finally {
      set({ checkingPayslip: false });
    }
  },

  rejectPayslip: async (id, data = {}) => {
    set({ rejectingPayslip: true, rejectingPayslipError: {}, rejectedPayslip: null });
    try {
      const res = await axios.patch(`/administration/monthly-payslips/reject/${id}`, data);
      set({ rejectedPayslip: res.data });
      get().getAllPayslips();
      get().getPayslipDetails(id);
    } catch (error) {
      set({ rejectingPayslipError: error });
    } finally {
      set({ rejectingPayslip: false });
    }
  },

  undoApprovePayslip: async (id) => {
    set({ undoingApprovePayslip: true, undoingApprovePayslipError: {}, undoneApprovePayslip: null });
    try {
      const res = await axios.patch(`/administration/monthly-payslips/undo-approve/${id}`);
      set({ undoneApprovePayslip: res.data });
      get().getAllPayslips();
      get().getPayslipDetails(id);
    } catch (error) {
      set({ undoingApprovePayslipError: error });
    } finally {
      set({ undoingApprovePayslip: false });
    }
  },

  downloadPayslipPdf: async (id) => {
    set({ downloadingPayslipPdf: true, downloadingPayslipPdfError: {} });
    try {
      const response = await axios.get(`/administration/monthly-payslips/download-pdf/${id}`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Payslip_${id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      set({ downloadingPayslipPdfError: error });
      throw error;
    } finally {
      set({ downloadingPayslipPdf: false });
    }
  },

  setCreatingPayslip: (show = false, creating = false, record = null) => {
    set({
      showCreatePayslip: show,
      creating: creating,
      payslipToEdit: record,
    });
  },

  setShowPayslipDetails: (show = false, record = null) => {
    set({
      showPayslipDetails: show,
      payslipDetailsRecord: record,
    });
  },
});

const useMonthlyPayslipStore = create(devtools(store));
export default useMonthlyPayslipStore; 